import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, X, Wallet, Eye, Send, BellRing, ArrowRightCircle, Play, Pause, CheckCircle2, XCircle, PackageCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, today, addDays, ugx } from "@/lib/format";
import { PageHeader, Panel, Empty, StatusPill } from "@/components/Shell";
import { AttachmentButton, AttachmentsDialog } from "@/components/Attachments";

const ENTITY = { invoices: "invoice", bills: "bill", "purchase-orders": "purchase_order" };

const emptyLine = () => ({ description: "", product_id: "", account_id: "", quantity: 1, unit_price: 0, vat_rate: 18 });

const FREQ = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

const DocumentsPage = ({ kind, partyKind, partyLabel, title, subtitle, testid, expenseSide }) => {
  const { canWrite } = useAuth();
  const isEstimate = kind === "estimates";
  const isRecurring = kind === "recurring-invoices";
  const isPO = kind === "purchase-orders";
  const isQuote = isEstimate || isPO;
  const singular = { invoices: "Invoice", bills: "Bill", estimates: "Estimate", "recurring-invoices": "Recurring schedule", "purchase-orders": "Purchase order" }[kind];
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ date: today(), amount: 0, payment_account_id: "", method: "bank", reference: "" });
  const [emailing, setEmailing] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const [counts, setCounts] = useState({});
  const entity = ENTITY[kind];
  const loadCounts = () => entity && api.get("/attachments/counts", { params: { entity_type: entity } }).then(({ data }) => setCounts(data));
  const blankForm = () => ({ party: "", date: today(), due_date: addDays(30), wht_rate: 0, notes: "", lines: [emptyLine()],
    frequency: "monthly", due_days: 30, end_date: "", auto_email: true });
  const [form, setForm] = useState(blankForm());

  const load = () => { loadCounts(); return api.get(`/${kind}`).then(({ data }) => setRows(data)); };

  useEffect(() => {
    load();
    api.get(`/${partyKind}`).then(({ data }) => setParties(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/accounts").then(({ data }) => setAccounts(data));
    /* eslint-disable-next-line */
  }, [kind]);

  const cashAccounts = accounts.filter((a) => ["1000", "1010", "1020"].includes(a.code));
  const expenseAccounts = accounts.filter((a) => a.type === "expense" || a.type === "asset");

  const totals = useMemo(() => {
    let subtotal = 0, vat = 0;
    form.lines.forEach((l) => {
      const amount = Number(l.quantity || 0) * Number(l.unit_price || 0);
      subtotal += amount;
      vat += (amount * Number(l.vat_rate || 0)) / 100;
    });
    const wht = (subtotal * Number(form.wht_rate || 0)) / 100;
    return { subtotal, vat, wht, total: subtotal + vat, net: subtotal + vat - wht };
  }, [form]);

  const setLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm({ ...form, lines });
  };

  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    setLine(idx, {
      product_id: productId,
      description: p ? p.name : "",
      unit_price: p ? (expenseSide ? p.cost_price : p.unit_price) : 0,
      vat_rate: p && !p.vat_applicable ? 0 : 18,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party) return toast.error(`Select a ${partyLabel.toLowerCase()}`);
    const payload = {
      [partyKind === "customers" ? "customer_id" : "vendor_id"]: form.party,
      date: form.date, due_date: form.due_date, wht_rate: Number(form.wht_rate || 0),
      notes: form.notes,
      ...(isEstimate ? { expiry_date: form.due_date } : {}),
      ...(isPO ? { expected_date: form.due_date } : {}),
      ...(isRecurring ? { next_date: form.date, frequency: form.frequency, due_days: Number(form.due_days || 30),
        end_date: form.end_date || null, auto_email: form.auto_email } : {}),
      lines: form.lines.map((l) => ({
        description: l.description, product_id: l.product_id || null,
        account_id: l.account_id || null, quantity: Number(l.quantity || 0),
        unit_price: Number(l.unit_price || 0), vat_rate: Number(l.vat_rate || 0),
      })),
    };
    try {
      await api.post(`/${kind}`, payload);
      toast.success(isQuote || isRecurring ? `${singular} saved` : `${singular} created and posted to the ledger`);
      setOpen(false);
      setForm(blankForm());
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/${kind}/${id}`); toast.success(isQuote || isRecurring ? "Deleted" : "Deleted and journal reversed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/${kind}/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const convert = async (row) => {
    try {
      const { data } = await api.post(isPO ? `/purchase-orders/${row.id}/receive` : `/estimates/${row.id}/convert`);
      toast.success(isPO ? `Received — bill ${data.number} created, stock updated and posted to the ledger`
        : `Converted to invoice ${data.number} and posted to the ledger`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const runNow = async (row) => {
    setEmailing(row.id);
    try {
      const { data } = await api.post(`/recurring-invoices/${row.id}/run`);
      toast.success(`Invoice ${data.invoice.number} created${data.emailed ? " and emailed" : ""}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setEmailing(null); }
  };

  const sendMail = async (row, endpoint) => {
    setEmailing(`${row.id}-${endpoint}`);
    try {
      const { data } = await api.post(`/invoices/${row.id}/${endpoint}`);
      toast.success(`${endpoint === "send-email" ? "Invoice" : "Reminder"} emailed to ${data.to}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setEmailing(null); }
  };

  const submitPayment = async (e) => {    e.preventDefault();
    try {
      await api.post(`/${kind}/${payFor.id}/payments`, { ...payForm, amount: Number(payForm.amount) });
      toast.success("Payment recorded");
      setPayFor(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader title={title} subtitle={subtitle}
        action={canWrite && (
          <button data-testid={`add-${testid}-button`} onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New {isPO ? "purchase order" : testid}
          </button>
        )} />

      <Panel title={`${rows.length} documents`}>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testid}-table`}>
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Number</th><th className="px-5 py-2.5">{partyLabel}</th>
                  <th className="px-5 py-2.5">{isRecurring ? "Frequency" : "Date"}</th>
                  <th className="px-5 py-2.5">{isRecurring ? "Next invoice" : isEstimate ? "Expires" : isPO ? "Expected" : "Due"}</th>
                  <th className="px-5 py-2.5 text-right">Subtotal</th><th className="px-5 py-2.5 text-right">VAT</th>
                  <th className="px-5 py-2.5 text-right">WHT</th>
                  <th className="px-5 py-2.5 text-right">{isRecurring ? "Amount / cycle" : isQuote ? "Total" : "Balance"}</th>
                  <th className="px-5 py-2.5">Status</th><th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-semibold text-slate-900">{r.number}</td>
                    <td className="px-5 py-3 text-slate-700">{r.customer_name || r.vendor_name}</td>
                    <td className="px-5 py-3 text-slate-600">{isRecurring ? FREQ[r.frequency] : shortDate(r.date)}</td>
                    <td className="px-5 py-3 text-slate-600">
                      {isRecurring ? shortDate(r.next_date) : shortDate(isEstimate ? r.expiry_date : isPO ? r.expected_date : r.due_date)}
                      {isRecurring && r.invoices_created > 0 && <span className="block text-xs text-slate-400">{r.invoices_created} sent · last {r.last_invoice_number}</span>}
                      {isEstimate && r.invoice_number && <span className="block text-xs text-indigo-600">→ {r.invoice_number}</span>}
                      {isPO && r.bill_number && <span className="block text-xs text-indigo-600">→ {r.bill_number}</span>}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.subtotal)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(r.vat_amount)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(r.wht_amount)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{money(r.amount_due)}</td>
                    <td className="px-5 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {entity && <AttachmentButton testid={`${testid}-attachments-button`} count={counts[r.id]} onClick={() => setAttachFor(r)} />}
                        {(kind === "invoices" || isQuote) && (
                          <Link to={`/${kind}/${r.id}`} data-testid={`view-${testid}-link`}
                            className="text-slate-400 hover:text-slate-900" aria-label={`View ${testid}`}><Eye size={15} /></Link>
                        )}
                        {canWrite && isPO && r.status !== "received" && (
                          <>
                            {r.status === "draft" && (
                              <button data-testid="mark-po-sent-button" onClick={() => setStatus(r.id, "sent")}
                                className="text-slate-400 hover:text-sky-600" aria-label="Mark as sent"><Send size={15} /></button>
                            )}
                            {r.status !== "cancelled" && (
                              <>
                                <button data-testid="cancel-po-button" onClick={() => setStatus(r.id, "cancelled")}
                                  className="text-slate-400 hover:text-rose-600" aria-label="Cancel order"><XCircle size={15} /></button>
                                <button data-testid="receive-po-button" onClick={() => convert(r)}
                                  className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-indigo-700">
                                  <PackageCheck size={13} /> Receive → Bill
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {canWrite && isEstimate && r.status !== "converted" && (
                          <>
                            {r.status === "draft" && (
                              <button data-testid="mark-estimate-sent-button" onClick={() => setStatus(r.id, "sent")}
                                className="text-slate-400 hover:text-sky-600" aria-label="Mark as sent"><Send size={15} /></button>
                            )}
                            {r.status !== "accepted" && r.status !== "declined" && (
                              <button data-testid="mark-estimate-accepted-button" onClick={() => setStatus(r.id, "accepted")}
                                className="text-slate-400 hover:text-emerald-600" aria-label="Mark accepted"><CheckCircle2 size={15} /></button>
                            )}
                            {r.status !== "declined" && (
                              <button data-testid="mark-estimate-declined-button" onClick={() => setStatus(r.id, "declined")}
                                className="text-slate-400 hover:text-rose-600" aria-label="Mark declined"><XCircle size={15} /></button>
                            )}
                            {r.status !== "declined" && (
                              <button data-testid="convert-estimate-button" onClick={() => convert(r)}
                                className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-indigo-700">
                                <ArrowRightCircle size={13} /> Invoice
                              </button>
                            )}
                          </>
                        )}
                        {canWrite && isRecurring && r.status !== "ended" && (
                          <>
                            <button data-testid="run-recurring-now-button" disabled={emailing === r.id} onClick={() => runNow(r)}
                              className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                              <Play size={13} /> Run now
                            </button>
                            <button data-testid="toggle-recurring-status-button" onClick={() => setStatus(r.id, r.status === "active" ? "paused" : "active")}
                              className="text-slate-400 hover:text-amber-600" aria-label={r.status === "active" ? "Pause" : "Resume"}>
                              {r.status === "active" ? <Pause size={15} /> : <Play size={15} />}
                            </button>
                          </>
                        )}
                        {canWrite && (isQuote || isRecurring) && r.status !== "converted" && r.status !== "received" && (
                          <button data-testid={`delete-${testid}-button`} onClick={() => remove(r.id)}
                            className="text-slate-400 hover:text-rose-600" aria-label="Delete"><Trash2 size={15} /></button>
                        )}
                        {canWrite && !isQuote && !isRecurring && (
                          <>
                            {kind === "invoices" && (
                              <>
                                <button data-testid="send-invoice-email-button" disabled={emailing === `${r.id}-send-email`}
                                  onClick={() => sendMail(r, "send-email")}
                                  className="text-slate-400 hover:text-sky-600 disabled:opacity-40" aria-label="Email invoice"><Send size={15} /></button>
                                {r.amount_due > 0.01 && (
                                  <button data-testid="send-reminder-button" disabled={emailing === `${r.id}-send-reminder`}
                                    onClick={() => sendMail(r, "send-reminder")}
                                    className="text-slate-400 hover:text-amber-600 disabled:opacity-40" aria-label="Send payment reminder"><BellRing size={15} /></button>
                                )}
                              </>
                            )}
                            {r.amount_due > 0.01 && (
                              <button data-testid={`pay-${testid}-button`}
                                onClick={() => { setPayFor(r); setPayForm({ date: today(), amount: r.amount_due, payment_account_id: cashAccounts[1]?.id || cashAccounts[0]?.id || "", method: "bank", reference: "" }); }}
                                className="text-slate-400 hover:text-emerald-600" aria-label="Record payment"><Wallet size={15} /></button>
                            )}
                            <button data-testid={`delete-${testid}-button`} onClick={() => remove(r.id)}
                              className="text-slate-400 hover:text-rose-600" aria-label="Delete"><Trash2 size={15} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message={`No ${kind} recorded yet.`} />}
      </Panel>

      {attachFor && (
        <AttachmentsDialog entityType={entity} entityId={attachFor.id} title={attachFor.number}
          onClose={() => setAttachFor(null)} onChanged={loadCounts} />
      )}

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <div className="mx-auto w-full max-w-4xl rounded-lg border border-slate-200 bg-white shadow-md" data-testid={`${testid}-dialog`}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">New {isPO ? "purchase order" : testid}</h3>
              <button data-testid={`close-${testid}-dialog`} onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{partyLabel}</label>
                  <select data-testid={`${testid}-party-select`} required value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select {partyLabel.toLowerCase()}…</option>
                    {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{isRecurring ? "First invoice date" : "Date"}</label>
                  <input data-testid={`${testid}-date-input`} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
                {isRecurring ? (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Frequency</label>
                    <select data-testid="recurring-frequency-select" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                      {Object.entries(FREQ).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{isEstimate ? "Valid until" : isPO ? "Expected delivery" : "Due date"}</label>
                    <input data-testid={`${testid}-due-date-input`} type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                  </div>
                )}
              </div>
              {isRecurring && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Payment terms (days)</label>
                    <input data-testid="recurring-due-days-input" type="number" min={0} value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">End date (optional)</label>
                    <input data-testid="recurring-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-slate-700 sm:col-span-2">
                    <input data-testid="recurring-auto-email-checkbox" type="checkbox" checked={form.auto_email} onChange={(e) => setForm({ ...form, auto_email: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600" />
                    Email each invoice to the customer automatically
                  </label>
                </div>
              )}

              <div className="mt-6 overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2">Item / Description</th>
                      {expenseSide && <th className="px-3 py-2">Account</th>}
                      <th className="px-3 py-2 w-20">Qty</th><th className="px-3 py-2 w-32">Unit price</th>
                      <th className="px-3 py-2 w-24">VAT %</th><th className="px-3 py-2 w-28 text-right">Amount</th><th /></tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <select data-testid={`line-product-select-${idx}`} value={l.product_id} onChange={(e) => pickProduct(idx, e.target.value)}
                            className="mb-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs">
                            <option value="">Free text line</option>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <input data-testid={`line-description-input-${idx}`} value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })}
                            placeholder="Description" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        {expenseSide && (
                          <td className="px-3 py-2">
                            <select data-testid={`line-account-select-${idx}`} value={l.account_id} onChange={(e) => setLine(idx, { account_id: e.target.value })}
                              className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs">
                              <option value="">Default expense</option>
                              {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                            </select>
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <input data-testid={`line-qty-input-${idx}`} type="number" step="any" value={l.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid={`line-price-input-${idx}`} type="number" step="any" value={l.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <select data-testid={`line-vat-select-${idx}`} value={l.vat_rate} onChange={(e) => setLine(idx, { vat_rate: e.target.value })}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm">
                            <option value={18}>18%</option><option value={0}>0% / Exempt</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {money(Number(l.quantity || 0) * Number(l.unit_price || 0))}
                        </td>
                        <td className="px-2">
                          {form.lines.length > 1 && (
                            <button type="button" data-testid={`remove-line-button-${idx}`}
                              onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}
                              className="text-slate-400 hover:text-rose-600" aria-label="Remove line"><Trash2 size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button type="button" data-testid="add-line-button" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
                className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">
                <Plus size={14} /> Add line
              </button>

              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Withholding tax rate (%)</label>
                  <select data-testid={`${testid}-wht-select`} value={form.wht_rate} onChange={(e) => setForm({ ...form, wht_rate: e.target.value })}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                    <option value={0}>No withholding tax</option>
                    <option value={6}>6% — goods & services (URA)</option>
                    <option value={15}>15% — professional / management fees</option>
                  </select>
                  <label className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">Notes</label>
                  <textarea data-testid={`${testid}-notes-input`} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
                  {[["Subtotal", totals.subtotal], ["VAT (18%)", totals.vat], ["Withholding tax", -totals.wht]].map(([label, v]) => (
                    <div key={label} className="flex justify-between py-1 text-slate-600">
                      <span>{label}</span><span className="tabular-nums">{money(v)}</span>
                    </div>
                  ))}
                  <div className="mt-2 flex justify-between border-t border-slate-300 pt-2 font-display text-base font-bold text-slate-900">
                    <span>{expenseSide ? "Amount payable" : isRecurring ? "Amount per invoice" : "Amount receivable"}</span>
                    <span className="tabular-nums" data-testid={`${testid}-total`}>{ugx(totals.net)}</span>
                  </div>
                </div>
              </div>

              <button data-testid={`save-${testid}-button`} className="mt-6 w-full rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                {isEstimate ? "Save estimate" : isPO ? "Save purchase order" : isRecurring ? "Save schedule" : "Save & post to ledger"}
              </button>
            </form>
          </div>
        </div>
      )}

      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <form onSubmit={submitPayment} className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="payment-dialog">
            <h3 className="font-display text-base font-bold text-slate-900">Record payment — {payFor.number}</h3>
            <p className="mt-1 text-sm text-slate-500">Outstanding {ugx(payFor.amount_due)}</p>
            <label className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">Date</label>
            <input data-testid="payment-date-input" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <label className="mb-1.5 mt-3 block text-sm font-medium text-slate-700">Amount (UGX)</label>
            <input data-testid="payment-amount-input" type="number" step="any" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <label className="mb-1.5 mt-3 block text-sm font-medium text-slate-700">Deposit to / paid from</label>
            <select data-testid="payment-account-select" required value={payForm.payment_account_id} onChange={(e) => setPayForm({ ...payForm, payment_account_id: e.target.value })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Select account…</option>
              {cashAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
            <div className="mt-5 flex gap-2">
              <button data-testid="save-payment-button" className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Record</button>
              <button type="button" data-testid="cancel-payment-button" onClick={() => setPayFor(null)}
                className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export const Invoices = () => (
  <DocumentsPage kind="invoices" partyKind="customers" partyLabel="Customer" testid="invoice"
    title="Sales Invoices" subtitle="Raise VAT invoices in UGX, apply withholding tax and collect payments." />
);

export const Estimates = () => (
  <DocumentsPage kind="estimates" partyKind="customers" partyLabel="Customer" testid="estimate"
    title="Estimates & Quotations" subtitle="Send quotations or pro-forma invoices, then convert accepted ones to tax invoices in one click." />
);

export const Recurring = () => (
  <DocumentsPage kind="recurring-invoices" partyKind="customers" partyLabel="Customer" testid="recurring"
    title="Recurring Invoices" subtitle="Rent, retainers, school fees and service contracts — invoiced and emailed automatically every cycle." />
);

export const PurchaseOrders = () => (
  <DocumentsPage kind="purchase-orders" partyKind="vendors" partyLabel="Vendor" testid="po" expenseSide
    title="Purchase Orders" subtitle="Raise LPOs to suppliers, then receive goods to create the bill and update stock in one click." />
);

export const Bills = () => (
  <DocumentsPage kind="bills" partyKind="vendors" partyLabel="Vendor" testid="bill" expenseSide
    title="Supplier Bills" subtitle="Record purchases, reclaim input VAT and track withholding tax due to URA." />
);
